import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface Aba<T extends string> {
  value: T;
  label: ReactNode;
  /** Contador em pílula; omitido ou null não mostra. */
  contador?: number | null;
  /** Pílula vermelha (pendências, erros), mesmo com a aba inativa. */
  alerta?: boolean;
  /** Pílula âmbar quando inativa e > 0 (ex.: "A validar" — pede ação, mas não é erro). */
  aviso?: boolean;
}

/** Abas com borda inferior laranja no item ativo e contador em pílula. */
export function AbasSublinhadas<T extends string>({
  value,
  onChange,
  abas,
  className,
}: {
  value: T;
  onChange: (v: NoInfer<T>) => void;
  abas: Aba<NoInfer<T>>[];
  className?: string;
}) {
  return (
    <div role="tablist" className={cn("flex gap-1 overflow-x-auto border-b", className)}>
      {abas.map((a) => {
        const ativo = a.value === value;
        return (
          <button
            key={a.value}
            type="button"
            role="tab"
            aria-selected={ativo}
            onClick={() => onChange(a.value)}
            className={cn(
              "-mb-px flex h-10 items-center gap-2 whitespace-nowrap border-b-2 px-3 text-sm font-medium transition-colors",
              ativo
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {a.label}
            {a.contador != null && (
              <span
                className={cn(
                  "inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-semibold tabular-nums",
                  a.alerta
                    ? "bg-destructive text-destructive-foreground"
                    : ativo
                      ? "bg-primary text-primary-foreground"
                      : a.aviso && a.contador > 0
                        ? "bg-amber-500/[0.18] text-amber-700 dark:text-amber-400"
                        : "bg-muted text-muted-foreground",
                )}
              >
                {a.contador}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
